/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { GET_POLICY_HOLDER_DETAIL } from '@/pages/HoldersDetailPage/api/graphql';
import { Graphql } from '@/common/graphql';
import { apiConfig } from '@/config/apiConfig';
import { AppNavigatorService } from '@/AppNavigator/services';
import { vappContainer } from '@/application';

import { useUserId } from '@pages/HoldersDetailPage/provider';

import { featureHolderDetailConfig } from '@pages/HoldersDetailPage/config/featuresSection.config';

import { toHeaderCase, toLowerCase, toSentenceCase } from 'js-convert-case';

export interface IfeatureHolderDetailPluginMethods extends IPluginMethodMap {
  getfeatureHolderDetail(args?: any): any;
}

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

const lookup = require('country-code-lookup');

export class featureHolderDetailPlugin implements IAgentPlugin {
  readonly methods: IfeatureHolderDetailPluginMethods;

  constructor() {
    this.methods = {
      getfeatureHolderDetail: this.getfeatureHolderDetail.bind(this),
    };
  }

  async getfeatureHolderDetail(args?: any) {
    return await this.holderDetail();
  }

  private async holderDetail() {
    const { userId } = useUserId();
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      // const token = getToken();
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client.query(GET_POLICY_HOLDER_DETAIL, {
        input: {
          vuid: userId,
        },
      });
      if (res?.data?.getUser) {
        let _res = res?.data?.getUser;
        return {
          title: `${toHeaderCase(_res?.firstName ?? '-')} ${toHeaderCase(
            _res?.lastName ?? '-'
          )}`,
          subtitle: _res?.occupation,
          kycSubmitted: _res?.kycSubmitted,
          profileStatus: this.getStatus(_res?.kyc),
          profileStatusDescription: this.getStatusDesc(_res?.kyc),
          heroImage: {
            image:
              _res?.image ??
              `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`,
          },
          listItems: this.getListItems(_res),
          carouselItems: _res?.kycSubmitted
            ? this.getCarouselItems(_res?.kyc)
            : null,
          buttons: await this.getButtons(_res),
        };
      }
    } catch (err: any) {
      return {
        isEmpty: true,
      };
    }
  }

  private getCarouselItems(items: any) {
    let _temp: any = [];
    _temp =
      Array.isArray(items) &&
      items?.map((ele: any) => {
        return {
          ...ele,
          type: this.getDocType(ele?.fileType),
          src: ele?.doc,
          title: ele?.name,
          poster: ele?.poster,
        };
      });
    return _temp;
  }

  private getDocType(res: any) {
    switch (res) {
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
        return 'img';
      case 'image/jpg':
        return 'img';
      case 'image/jpeg':
        return 'img';
      default:
        return null;
    }
  }

  private async getButtons(res: any) {
    let _buttons: any = [];
    let _temp1 = res.kyc?.find((i: any) => i?.type === 'ADDRESS_PROOF');
    let whoAmI = await loginSrv()?.whoAmI();
    if (whoAmI?.whoami === 'RISK_CAPITAL') {
      return [];
    } else if (_temp1?.status === 'SUBMITTED') {
      _buttons.push(featureHolderDetailConfig?.approveButton);
    } else if (_temp1?.status === 'REJECTED') {
      _buttons.push(featureHolderDetailConfig?.approveButton);
    }
    return _buttons;
  }

  private getStatus(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.status) {
      return toLowerCase(tempStatus?.status ?? '');
    } else return null;
  }

  private getStatusDesc(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.comment) {
      return toSentenceCase(tempStatus?.comment ?? '');
    } else return null;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = featureHolderDetailConfig?.listItems?.map((item: any) => {
      if (item?.title === 'Full Name') {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? '')} ${toHeaderCase(
            res?.lastName ?? ''
          )}`,
        };
      }
      if (item?.title === 'Street Address') {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : '-',
        };
      }
      if (item?.title === 'City') {
        return {
          ...item,
          subTitle: res?.address?.city ?? '-',
        };
      }
      if (item?.title === 'Country') {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : '-',
          flag: res?.address?.country,
        };
      }
      if (item?.title === 'Email') {
        return {
          ...item,
          subTitle: res?.email ?? '-',
          verified: res?.emailVerified,
        };
      }
      if (item?.title === 'Telephone') {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? '-',
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === 'Occupation') {
        return {
          ...item,
          subTitle: res?.occupation ?? '-',
        };
      }
    });
    return _temp;
  }
}
